import React, { FunctionComponent, useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import logos from '../../media/logos-flag.png';
import controlColorWhite from '../../media/control-color-white.png';
import controlColorDark from '../../media/control-color-dark.png';
import controlColorBlack from '../../media/control-color-black.png';
import controlGenreMale from '../../media/control-genre-male.png';
import controlGenreFemale from '../../media/control-genre-female.png';
import controlSkinNature from '../../media/control-skin-nature.png';
import controlSkinDanta from '../../media/control-skin-danta.png';
import controlSkinMonkey from '../../media/control-skin-monkey.png';
import controlSkinTurkey from '../../media/control-skin-turkey.png';
import controlSkinBird from '../../media/control-skin-bird.png';
import controlArrowLeft from '../../media/control-arrow-left.png';
import controlButtonOk from '../../media/control-button-ok.png';
import controlButtonSound from '../../media/control-sound.png';
import Character from '../../components/character/Character';
import GameContext from '../../context/GameContext';
import Information from '../../components/common/Information';

export type Skin = 'nature' | 'danta' | 'monkey' | 'turkey' | 'bird';
export type Color = 'white' | 'dark' | 'black';
export type Genre = 'male' | 'female';

export type CharacterSeed = {
    skin: Skin;
    color: Color;
    genre: Genre;
};

type SkinInformation = {
    [key in Skin]: string | null;
};

const skinDescriptions: SkinInformation = {
    nature: '¡Elige un disfraz de la fauna de la Cuenca para aprender más!',
    danta: 'También conocido como Tapirus, es uno de los mamíferos nativos más grande del centro y suramérica. Su rol en la selva y entornos naturales es muy importante debido a su papel como herbívoro dispersor de semillas',
    monkey: 'Se destaca por su potente aullido, que utiliza para marcar territorio y advertir su presencia a otros grupos vecinos, también para competir por una hembra al momento de aparearse o fortalecer los lazos de pareja. Conviven en manadas de 6 y se encargar de esparcir semillar en sus caminatas de 1 kilómetro o kilómetro y medio aproximadamente.',
    turkey: 'Normalmente es un ave solitaria, sin embargo, debido a la oferta de un determinado recurso como los cultivos de Urapán, pueden formar congregaciones localizadas. Generalmente, se encuentran en la parte media y alta de los árboles, rara vez bajan al suelo. En el Santuario de Fauna y Flora Otún Quimbaya, se encontró que habita en todos los tipos de coberturas vegetales de la reserva.',
    bird: 'También reconocido en Colombia como Yacutoro, Sangretoro o Pájaro Torero, se encuentra distribuido en los países de Suramérica. Gracias a que se alimenta de semillas y frutos, se convierte en un dispersor que contribuye a la regeneración del bosque húmedo tropical.',
};

const skinTitles: SkinInformation = {
    nature: null,
    danta: 'Danta',
    monkey: 'Mono Aullador',
    turkey: 'Pava Caucana',
    bird: 'Torito de Monte',
};

const skinDialogs: SkinInformation = {
    nature: null,
    danta: 'skin__danta',
    monkey: 'skin__monkey',
    turkey: 'skin__turkey',
    bird: 'skin__bird',
};

interface AvatarProps {}

const Avatar: FunctionComponent<AvatarProps> = () => {
    const history = useHistory();
    const { avatarSeed, playerName, handleSetAvatar, handleMainSound, handleDialogSound } = useContext(GameContext);

    const [skin, setSkin] = useState<Skin>(avatarSeed.skin);
    const [color, setColor] = useState<Color>(avatarSeed.color);
    const [genre, setGenre] = useState<Genre>(avatarSeed.genre);

    const handleNavigateToLobbyPage = () => {
        history.goBack();
    };

    const handleSaveAvatar = () => {
        handleSetAvatar({ skin, color, genre });
        handleNavigateToLobbyPage();
    };

    const handleChangeSkin = (newSkin: Skin) => {
        const oldDialog = skinDialogs[skin];

        if (oldDialog && handleDialogSound) {
            const [, control] = handleDialogSound[oldDialog];
            control.stop();
        }

        setSkin(newSkin);
    };

    const handleDialogPlay = (id: string | null) => {
        if (id && handleDialogSound) {
            handleMainSound('stop-avatar-music');

            const [playDialog] = handleDialogSound[id];
            playDialog();
        }
    };

    useEffect(() => {
        handleMainSound('play-avatar-music');
        return () => handleMainSound('stop-avatar-music');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <div className="page-container page-avatar animate__animated animate__fadeIn">
                <img
                    src={logos}
                    className="common-logos animate__animated animate__slideInDown"
                    alt="Héroes de la Cuenca"
                />
                <div className="character-container">
                    <div className="wood-title big animate__animated animate__rubberBand">
                        <span className="character-name-input">{playerName}</span>
                    </div>
                    <div className="character-control-vertical-group left animate__animated animate__bounce">
                        <img
                            className={color === 'white' ? 'character-control-active' : 'animate__custom_hover_scale'}
                            src={controlColorWhite}
                            alt="blanco"
                            onClick={() => setColor('white')}
                            onMouseEnter={() => handleMainSound('play-pop-effect')}
                        />
                        <img
                            className={color === 'dark' ? 'character-control-active' : 'animate__custom_hover_scale'}
                            src={controlColorDark}
                            alt="trigueño"
                            onClick={() => setColor('dark')}
                            onMouseEnter={() => handleMainSound('play-pop-effect')}
                        />
                        <img
                            className={color === 'black' ? 'character-control-active' : 'animate__custom_hover_scale'}
                            src={controlColorBlack}
                            alt="negro"
                            onClick={() => setColor('black')}
                            onMouseEnter={() => handleMainSound('play-pop-effect')}
                        />
                    </div>
                    <div className="character-control-vertical-group right animate__animated animate__bounce">
                        <img
                            className={genre === 'male' ? 'character-control-active' : 'animate__custom_hover_scale'}
                            src={controlGenreMale}
                            alt="masculino"
                            onClick={() => setGenre('male')}
                            onMouseEnter={() => handleMainSound('play-pop-effect')}
                        />
                        <img
                            className={genre === 'female' ? 'character-control-active' : 'animate__custom_hover_scale'}
                            src={controlGenreFemale}
                            alt="femenino"
                            onClick={() => setGenre('female')}
                            onMouseEnter={() => handleMainSound('play-pop-effect')}
                        />
                    </div>

                    <div className="character-container-avatar animate__animated animate__tada animate__delay-1s">
                        <Character
                            seed={{
                                skin,
                                color,
                                genre,
                            }}
                        />
                    </div>
                    <div className="character-skin-description">
                        {skinTitles[skin] && <span className="skin-title">{skinTitles[skin]}</span>}
                        <p>"{skinDescriptions[skin]}"</p>
                        {skinDialogs[skin] && (
                            <img
                                src={controlButtonSound}
                                className="character-skin-audio  animate__animated animate__heartBeat animate__infinite"
                                alt="Reproducir sonido"
                                onClick={() => handleDialogPlay(skinDialogs[skin])}
                            />
                        )}
                    </div>
                    <div className="character-control-horizontal-group animate__animated animate__bounce animate__faster">
                        <img
                            className={
                                skin === 'nature'
                                    ? 'character-control-active character-skin-active'
                                    : 'animate__custom_hover_scale'
                            }
                            src={controlSkinNature}
                            alt="Naturaleza"
                            onClick={() => handleChangeSkin('nature')}
                            onMouseEnter={() => handleMainSound('play-pop-effect')}
                        />
                        <img
                            className={
                                skin === 'danta'
                                    ? 'character-control-active character-skin-active'
                                    : 'animate__custom_hover_scale'
                            }
                            src={controlSkinDanta}
                            alt="Danta"
                            onClick={() => handleChangeSkin('danta')}
                            onMouseEnter={() => handleMainSound('play-pop-effect')}
                        />
                        <img
                            className={
                                skin === 'monkey'
                                    ? 'character-control-active character-skin-active'
                                    : 'animate__custom_hover_scale'
                            }
                            src={controlSkinMonkey}
                            alt="Mono"
                            onClick={() => handleChangeSkin('monkey')}
                            onMouseEnter={() => handleMainSound('play-pop-effect')}
                        />
                        <img
                            className={
                                skin === 'turkey'
                                    ? 'character-control-active character-skin-active'
                                    : 'animate__custom_hover_scale'
                            }
                            src={controlSkinTurkey}
                            alt="Pava"
                            onClick={() => handleChangeSkin('turkey')}
                            onMouseEnter={() => handleMainSound('play-pop-effect')}
                        />
                        <img
                            className={
                                skin === 'bird'
                                    ? 'character-control-active character-skin-active'
                                    : 'animate__custom_hover_scale'
                            }
                            src={controlSkinBird}
                            alt="Torito"
                            onClick={() => handleChangeSkin('bird')}
                            onMouseEnter={() => handleMainSound('play-pop-effect')}
                        />
                    </div>
                    <img
                        src={controlArrowLeft}
                        className="button-return animate__custom_hover_scale"
                        alt="Regresar"
                        onClick={handleNavigateToLobbyPage}
                        onMouseEnter={() => handleMainSound('play-pop-effect')}
                    />
                    <img
                        src={controlButtonOk}
                        className="button-save-character animate__custom_hover_scale"
                        alt="Continuar"
                        onClick={handleSaveAvatar}
                        onMouseEnter={() => handleMainSound('play-pop-effect')}
                    />
                </div>
            </div>
            <Information helpText="Personaliza tu avatar haciendo clic en los botones alrededor del mismo, puedes elegir tus preferencias de tono de piel, género o incluso el disfraz que más te identifique. Una vez te encuentres a gusto, haz clic en el ícono inferior derecho de la pantalla para guardar o, haz clic en el ícono inferior izquierdo para descartar los ajustes realizados y volver al Lobby." />
        </>
    );
};

export default Avatar;
